import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { platformAdminAPI } from '../../services/api';
import { extractVariables } from '../../utils/promptVariables';

const PROVIDER_OPTIONS = ['anthropic', 'openai', 'together', 'fireworks', 'ollama', 'custom'];
const GUIDE_STORAGE_KEY = 'promptsGuideCollapsed';

function formatTaskType(taskType) {
  return taskType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function PromptsManagement() {
  const [prompts, setPrompts] = useState([]);
  const [selectedTaskType, setSelectedTaskType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);

  // Editor form state
  const [description, setDescription] = useState('');
  const [provider, setProvider] = useState('anthropic');
  const [modelId, setModelId] = useState('');
  const [maxTokens, setMaxTokens] = useState(4096);
  const [temperature, setTemperature] = useState(0.7);
  const [enabled, setEnabled] = useState(true);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPromptTemplate, setUserPromptTemplate] = useState('');
  const [versionNotes, setVersionNotes] = useState('');

  const [guideCollapsed, setGuideCollapsed] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage?.getItem(GUIDE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const detectedVariables = useMemo(
    () => extractVariables(`${systemPrompt}\n${userPromptTemplate}`),
    [systemPrompt, userPromptTemplate]
  );

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await platformAdminAPI.listPrompts();
      const list = res.data || [];
      setPrompts(list);
      if (list.length > 0 && !selectedTaskType) {
        selectPrompt(list[0].task_type);
      }
    } catch (err) {
      setError('Failed to load prompts: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const selectPrompt = async (taskType) => {
    setSelectedTaskType(taskType);
    try {
      setDetailLoading(true);
      setError(null);
      setSuccess(null);
      const res = await platformAdminAPI.getPrompt(taskType);
      const data = res.data || res;

      // Populate form
      setDescription(data.description || '');
      setProvider(data.provider || 'anthropic');
      setModelId(data.model_id || '');
      setMaxTokens(data.max_tokens ?? 4096);
      setTemperature(data.temperature ?? 0.7);
      setEnabled(data.enabled !== false);
      setSystemPrompt(data.system_prompt || data.active_prompt_version?.system_prompt || '');
      setUserPromptTemplate(data.user_prompt_template || data.active_prompt_version?.user_prompt_template || '');
      setVersionNotes('');
    } catch (err) {
      setError('Failed to load prompt detail: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedTaskType) return;
    try {
      setSavingConfig(true);
      setError(null);
      setSuccess(null);
      await platformAdminAPI.updatePromptConfig(selectedTaskType, {
        description,
        provider,
        modelId,
        maxTokens: parseInt(maxTokens, 10),
        temperature: parseFloat(temperature),
        enabled
      });
      setSuccess('Model config saved successfully');
      await loadPrompts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save config: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleGuide = () => {
    const next = !guideCollapsed;
    setGuideCollapsed(next);
    try {
      window.localStorage?.setItem(GUIDE_STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* storage unavailable — fall back to in-memory state */
    }
  };

  const handleCopyVariable = async (varName) => {
    const placeholder = `{${varName}}`;
    try {
      await navigator.clipboard.writeText(placeholder);
      setSuccess(`Copied ${placeholder} to clipboard`);
      setTimeout(() => setSuccess(null), 1500);
    } catch {
      setError('Copy to clipboard failed — your browser may have blocked it.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleSaveVersion = async () => {
    if (!selectedTaskType) return;
    try {
      setSavingVersion(true);
      setError(null);
      setSuccess(null);
      const versionRes = await platformAdminAPI.createPromptVersion(selectedTaskType, {
        systemPrompt,
        userPromptTemplate,
        notes: versionNotes || undefined
      });
      const newVersion = versionRes.data || versionRes;
      const newVersionId = newVersion.id;

      // Auto-activate the new version
      if (newVersionId) {
        await platformAdminAPI.setActivePromptVersion(selectedTaskType, newVersionId);
      }

      setSuccess('Prompt version saved and activated');
      setVersionNotes('');
      await loadPrompts();
      await selectPrompt(selectedTaskType);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save prompt version: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setSavingVersion(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading prompts...</div>;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>AI Prompt Management</h1>
        <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
      </div>

      <div style={{
        marginBottom: '1rem',
        border: '1px solid var(--border-color, #ddd)',
        borderRadius: '8px',
        background: 'var(--bg-warm, #fffaf8)',
        overflow: 'hidden'
      }}>
        <button
          type="button"
          onClick={toggleGuide}
          aria-expanded={!guideCollapsed}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.75rem 1rem',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
            textAlign: 'left',
            color: 'var(--text-primary, #23201C)'
          }}
        >
          <span>How this page works — quick guide</span>
          <span style={{ fontSize: '0.8rem', color: 'var(--accent-color, #8F432F)' }}>
            {guideCollapsed ? '▸ Show' : '▾ Hide'}
          </span>
        </button>
        {!guideCollapsed && (
          <div style={{
            padding: '0 1rem 1rem',
            borderTop: '1px solid var(--border-color, #eee)',
            fontSize: '0.8125rem',
            lineHeight: 1.55,
            color: 'var(--text-primary, #23201C)'
          }}>
            <p style={{ marginTop: '0.75rem' }}>
              This page controls the AI behind quote extraction, follow-up drafts, and completeness
              analysis. Each row on the left is one task the AI can do.
            </p>
            <p><strong>What you can change</strong></p>
            <ul style={{ marginLeft: '1.2rem' }}>
              <li><strong>Model config</strong> (top of the editor) — which model, how creative, answer length. "Save Model Config" applies instantly.</li>
              <li><strong>System Prompt</strong> — the AI's personality and rules. Stays fixed across every run.</li>
              <li><strong>User Prompt Template</strong> — what the AI is actually asked to do. Can include <code>{'{variables}'}</code> that are replaced at runtime.</li>
              <li>Saving the prompt creates a <strong>new version</strong>. A running version is never overwritten — it's safe to experiment.</li>
            </ul>
            <p><strong>Variables</strong></p>
            <p>
              Anything inside curly braces like <code>{'{emailText}'}</code> is a variable — real data is
              inserted at runtime. The "Variables in this prompt" panel (just below the User Prompt
              Template) shows every variable detected in the currently-selected prompt and updates
              live as you edit. Click any chip to copy its <code>{'{name}'}</code> form.
            </p>
            <p><strong>Tips</strong></p>
            <ul style={{ marginLeft: '1.2rem' }}>
              <li>Start with small tweaks. Save often.</li>
              <li><strong>Temperature</strong> near 0 = consistent. Near 1 = creative. Production usually runs at 0 or 0.2.</li>
              <li>Turn a task off entirely with the <strong>Enabled</strong> checkbox — handy as an emergency stop.</li>
              <li>Every save is kept — history is stored even though this page only shows the active version.</li>
            </ul>
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '6px', background: 'rgba(139,58,58,0.1)', border: '1px solid #8B3A3A', color: '#8B3A3A', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '6px', background: 'rgba(92,122,84,0.1)', border: '1px solid #5C7A54', color: '#5C7A54', fontSize: '0.875rem' }}>
          {success}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        {/* Left panel — prompt list */}
        <div className="form-section" style={{ width: '280px', flexShrink: 0, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color, #ddd)', fontSize: '0.8rem', fontWeight: 600 }}>
            Prompt Configs ({prompts.length})
          </div>
          {prompts.map(p => (
            <div
              key={p.task_type}
              onClick={() => selectPrompt(p.task_type)}
              style={{
                padding: '0.6rem 1rem',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-color, #eee)',
                background: p.task_type === selectedTaskType ? 'var(--bg-warm, #fffaf8)' : 'transparent',
                borderLeft: p.task_type === selectedTaskType ? '3px solid var(--accent-color, #8F432F)' : '3px solid transparent'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>{formatTaskType(p.task_type)}</span>
                <span style={{
                  fontSize: '0.65rem',
                  padding: '0.1rem 0.35rem',
                  borderRadius: '3px',
                  fontWeight: 600,
                  background: p.enabled !== false ? 'rgba(92,122,84,0.15)' : 'rgba(162,162,153,0.15)',
                  color: p.enabled !== false ? '#5C7A54' : '#999'
                }}>
                  {p.enabled !== false ? 'ON' : 'OFF'}
                </span>
              </div>
              <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '0.15rem' }}>
                {p.description || p.task_type}
              </div>
            </div>
          ))}
          {prompts.length === 0 && (
            <div className="text-muted" style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.8rem' }}>
              No prompt configs found.
            </div>
          )}
        </div>

        {/* Right panel — editor */}
        <div className="form-section" style={{ flex: 1, minWidth: 0 }}>
          {!selectedTaskType || detailLoading ? (
            <div className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>
              {detailLoading ? 'Loading...' : 'Select a prompt config from the left panel.'}
            </div>
          ) : (
            <>
              <h2 style={{ marginTop: 0 }}>{formatTaskType(selectedTaskType)}</h2>

              {/* Model config fields */}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Task Type</label>
                  <input
                    type="text"
                    className="filter-input"
                    value={selectedTaskType}
                    disabled
                    style={{ width: '100%', opacity: 0.6 }}
                  />
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Description</label>
                  <input
                    type="text"
                    className="filter-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Provider</label>
                  <select
                    className="filter-input"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    {PROVIDER_OPTIONS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Model ID</label>
                  <input
                    type="text"
                    className="filter-input"
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ flex: '0 0 120px' }}>
                  <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Max Tokens</label>
                  <input
                    type="number"
                    className="filter-input"
                    value={maxTokens}
                    onChange={(e) => setMaxTokens(e.target.value)}
                    min="1"
                    max="100000"
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ flex: '0 0 100px' }}>
                  <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Temperature</label>
                  <input
                    type="number"
                    className="filter-input"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                    step="0.05"
                    min="0"
                    max="2"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  Enabled
                </label>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSaveConfig}
                disabled={savingConfig}
                style={{ marginBottom: '1.5rem' }}
              >
                {savingConfig ? 'Saving...' : 'Save Model Config'}
              </button>

              <hr style={{ border: 'none', borderTop: '1px solid var(--border-color, #ddd)', margin: '1rem 0' }} />

              {/* Prompt textareas */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>System Prompt</label>
                <textarea
                  className="filter-input"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={8}
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8rem', minHeight: '200px', resize: 'vertical' }}
                />
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>User Prompt Template</label>
                <textarea
                  className="filter-input"
                  value={userPromptTemplate}
                  onChange={(e) => setUserPromptTemplate(e.target.value)}
                  rows={8}
                  style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8rem', minHeight: '200px', resize: 'vertical' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.35rem' }}>
                  Variables in this prompt ({detectedVariables.length})
                </label>
                {detectedVariables.length === 0 ? (
                  <div className="text-muted" style={{
                    fontSize: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px dashed var(--border-color, #ddd)',
                    borderRadius: '6px',
                    background: 'var(--bg-soft, #fafaf8)'
                  }}>
                    No variables detected. Add one with the <code>{'{variableName}'}</code> syntax in either prompt above.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {detectedVariables.map(v => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => handleCopyVariable(v)}
                        title={`Click to copy {${v}}`}
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.6rem',
                          borderRadius: '999px',
                          border: '1px solid var(--accent-color, #8F432F)',
                          background: 'rgba(143,67,47,0.08)',
                          color: 'var(--accent-color, #8F432F)',
                          cursor: 'pointer'
                        }}
                      >
                        {`{${v}}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Version Notes (optional)</label>
                <input
                  type="text"
                  className="filter-input"
                  value={versionNotes}
                  onChange={(e) => setVersionNotes(e.target.value)}
                  placeholder="Brief note about this version..."
                  style={{ width: '100%' }}
                />
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSaveVersion}
                disabled={savingVersion}
              >
                {savingVersion ? 'Saving...' : 'Save Prompt as New Version'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
